import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Star as StarIcon, ArrowRight, BarChart, CheckCircle2 } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { Report, ReportType } from '../../types';
import ModernEvaluationModal from './ModernEvaluationModal';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';
import { EmptyState } from '../../components/common/EmptyState';

const typeIcon: { [key in ReportType]: string } = {
    [ReportType.Sales]: '📈',
    [ReportType.Maintenance]: '🔧',
    [ReportType.Project]: '🏗️',
    [ReportType.Inquiry]: '❓',
};

const ModernServiceEvaluationScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const { reports, updateReport } = useAppStore();
    const navigate = useNavigate();
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const handleSaveEvaluation = async (updatedReport: Report) => {
        if (!user) return;
        await updateReport(updatedReport);
        setSelectedReport(null);
        toast.success('تم حفظ التقييم وإرسال إشعار للموظف!');
    };

    // Prioritize un-evaluated reports and filter
    const filteredReports = reports
        .filter(r =>
        (r.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.type.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        .sort((a, b) => (a.evaluation ? 1 : -1) - (b.evaluation ? 1 : 0));

    const stats = {
        total: reports.length,
        evaluated: reports.filter(r => r.evaluation).length,
        pending: reports.filter(r => !r.evaluation).length,
        averageRating: reports.reduce((acc, r) => acc + (r.evaluation?.rating || 0), 0) / (reports.filter(r => r.evaluation).length || 1)
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">{t('serviceEvaluation')}</h1>
                    <p className="text-slate-400">تقييم أداء الموظفين وجودة التقارير</p>
                </div>
                <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                >
                    <ArrowRight size={18} />
                    <span>رجوع</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">إجمالي التقارير</p>
                        <p className="text-3xl font-bold text-white">{stats.total}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                        <BarChart size={24} />
                    </div>
                </div>
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">تم التقييم</p>
                        <p className="text-3xl font-bold text-white">{stats.evaluated}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                        <CheckCircle2 size={24} />
                    </div>
                </div>
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">بانتظار التقييم</p>
                        <p className="text-3xl font-bold text-white">{stats.pending}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400">
                        <StarIcon size={24} />
                    </div>
                </div>
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">متوسط التقييم</p>
                        <p className="text-3xl font-bold text-white">{stats.averageRating.toFixed(1)}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400">
                        <StarIcon size={24} className="fill-current" />
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="glass-card rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                    <h2 className="text-xl font-bold text-white">قائمة التقارير</h2>
                    <div className="relative w-full md:w-96">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="بحث في أسماء الموظفين والتقارير..."
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                </div>

                {filteredReports.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left rtl:text-right text-slate-300">
                            <thead className="text-xs uppercase bg-slate-800/50 text-slate-400">
                                <tr>
                                    <th scope="col" className="px-6 py-4">الموظف</th>
                                    <th scope="col" className="px-6 py-4">نوع التقرير</th>
                                    <th scope="col" className="px-6 py-4">التاريخ</th>
                                    <th scope="col" className="px-6 py-4">التقييم الحالي</th>
                                    <th scope="col" className="px-6 py-4">إجراء</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {filteredReports.map((report) => (
                                    <tr key={report.id} className={`hover:bg-white/5 transition-colors ${!report.evaluation ? 'bg-amber-500/5' : ''}`}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold border border-white/10">
                                                    {report.employeeName.charAt(0)}
                                                </div>
                                                <span className="font-medium text-white">{report.employeeName}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="flex items-center gap-2">
                                                <span>{typeIcon[report.type]}</span>
                                                <span>{report.type}</span>
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-400">
                                            {new Date(report.date).toLocaleDateString('ar-SA')}
                                        </td>
                                        <td className="px-6 py-4">
                                            {report.evaluation ? (
                                                <div className="flex items-center gap-1" dir="ltr">
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <StarIcon
                                                            key={i}
                                                            size={16}
                                                            className={`${i < report.evaluation!.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`}
                                                        />
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                                    بانتظار التقييم
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => setSelectedReport(report)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${report.evaluation
                                                    ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                                                    : 'bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20'
                                                    }`}
                                            >
                                                {report.evaluation ? 'تعديل التقييم' : 'إضافة تقييم'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <EmptyState
                        icon={StarIcon}
                        title="لا توجد تقارير"
                        message="لم يتم العثور على تقارير مطابقة للبحث."
                    />
                )}
            </div>

            <ModernEvaluationModal
                report={selectedReport}
                onClose={() => setSelectedReport(null)}
                onSave={handleSaveEvaluation}
            />
        </div>
    );
};

export default ModernServiceEvaluationScreen;
